import { strictify } from '~/utils/strictify'
import { createRequestOperation } from '~/utils/createRequestOperation'
import type { ResponseWrapper } from '~/services/api/interfaces/Common'
import { DashboardBrands } from '~/services/api/contracts/requests/DashboardBrands'
import { brandStoreSearchContract } from '~/services/api/contracts/brandStoreSearchContract'
import * as operations from '~/services/api/generated'
import type { BrandDetail, BrandPublicRecord } from '~/services/api/generated'
import type { Brand, BrandPage, FilterBrandRequest } from '~/services/api/interfaces/Brand'

// hey-api's zod plugin drops the OpenAPI-level bbox/place_type co-requirement; restored here.
const storeSearchQuerySchema = strictify(operations.BrandsPublicStoreSearchQuerySchema).superRefine(
  (query, context) => {
    const hasBbox = query.bbox !== null && query.bbox !== undefined
    const hasPlaceType = query.place_type !== null && query.place_type !== undefined
    if (hasBbox !== hasPlaceType) {
      context.addIssue({ code: 'custom', message: 'bbox-place-type-required-together' })
    }
  }
)

const normalizeBrandStore = (
  value: BrandDetail['stores'][number] | BrandPublicRecord['stores'][number]
): NonNullable<Brand['stores']>[number] => {
  const averageRating = 'average_rating' in value ? value.average_rating : null
  const wireReviewCount = 'review_count' in value ? (value.review_count ?? 0) : 0
  return {
    id: value.id,
    name: value.name,
    slug: value.slug,
    cover: 'cover' in value ? value.cover : null,
    logo: 'logo' in value ? value.logo : null,
    address: 'address' in value ? value.address : null,
    city: value.city,
    state: value.state,
    ...('formatted_address' in value && value.formatted_address !== null
      ? { formatted_address: value.formatted_address }
      : {}),
    // A null average_rating means no rating exists; review_count is forced to 0 alongside it so
    // a wire inconsistency (a stray positive count with no rating) can never render a blank number.
    average_rating: averageRating,
    review_count: averageRating === null ? 0 : wireReviewCount,
    delivery_fee: 'delivery_fee' in value ? value.delivery_fee : null,
    dine_in_available: 'dine_in_available' in value ? value.dine_in_available : false
  }
}

const normalizeBrand = (value: BrandDetail | BrandPublicRecord): Brand => ({
  id: value.id,
  name: value.name,
  slug: value.slug,
  ...(value.description === null ? {} : { description: value.description }),
  ...(value.logo === null ? {} : { logo: value.logo }),
  ...(value.cover === null ? {} : { cover: value.cover }),
  status: 'status' in value ? value.status : '',
  ...('website' in value && value.website !== null ? { website: value.website } : {}),
  is_featured: value.is_featured,
  ...('created_at' in value && value.created_at !== null ? { created_at: value.created_at } : {}),
  ...('updated_at' in value && value.updated_at !== null ? { updated_at: value.updated_at } : {}),
  stores: value.stores.map(normalizeBrandStore),
  stores_count: value.stores_count
})

const normalizeBrandIndexResponse = (response: operations.BrandsIndexSuccess): BrandPage => ({
  data: response.data.brands.map(normalizeBrand),
  pagination: response.data.pagination
})

const normalizeDashboardBrandResponse = (
  response: operations.DashboardBrandsSuccess
): BrandPage => ({
  data: response.data.brands.map(normalizeBrand),
  pagination: response.data.pagination
})

const normalizePublicBrandIndexResponse = (
  response: operations.BrandsPublicIndexSuccess
): BrandPage => ({
  data: response.data.brands.map(normalizeBrand),
  pagination: response.data.pagination
})

const normalizeBrandResponse = (
  response: operations.BrandsShowSuccess
): ResponseWrapper<Brand> => ({ data: normalizeBrand(response.data) })

const normalizePublicBrandResponse = (
  response: operations.BrandsPublicShowSuccess
): ResponseWrapper<Brand | null> => ({
  data: response.data === null ? null : normalizeBrand(response.data)
})

export function useBrandService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl}/api/brands`
  const dashboardBaseURL = `${useRuntimeConfig().public.apiBaseUrl}/api/dashboard/brands`
  const publicBaseURL = `${useRuntimeConfig().public.apiBaseUrl}/api/brands/public`

  const client = useApiRequest()

  return {
    list: createRequestOperation(
      client,
      operations.BrandsIndexSuccessSchema,
      (params?: FilterBrandRequest) => ({
        url: baseURL,
        options: {
          method: 'GET',
          params: operations.BrandsIndexQuerySchema.strict().parse(params ?? {})
        }
      }),
      normalizeBrandIndexResponse
    ),
    dashboardList: createRequestOperation(
      client,
      operations.DashboardBrandsSuccessSchema,
      (params?: operations.DashboardBrandsQuery) => ({
        url: dashboardBaseURL,
        options: { method: 'GET', params: DashboardBrands({}).parse(params ?? {}) }
      }),
      normalizeDashboardBrandResponse
    ),
    show: createRequestOperation(
      client,
      operations.BrandsShowSuccessSchema,
      (id: number) => ({
        url: `${baseURL}/${operations.BrandsShowPathSchema.strict().parse({ identifier: id.toString() }).identifier}`,
        options: { globalErrorUi: true, method: 'GET' }
      }),
      normalizeBrandResponse
    ),
    publicList: createRequestOperation(
      client,
      operations.BrandsPublicIndexSuccessSchema,
      (params?: operations.BrandsPublicIndexQuery) => ({
        url: publicBaseURL,
        options: {
          method: 'GET',
          params: operations.BrandsPublicIndexQuerySchema.strict().parse(params ?? {})
        }
      }),
      normalizePublicBrandIndexResponse
    ),
    publicShow: createRequestOperation(
      client,
      operations.BrandsPublicShowSuccessSchema,
      (slug: string) => ({
        url: `${publicBaseURL}/${operations.BrandsPublicShowPathSchema.strict().parse({ slug }).slug}`,
        options: { globalErrorUi: true, method: 'GET' }
      }),
      normalizePublicBrandResponse
    ),
    publicStoreSearch: createRequestOperation(
      client,
      operations.BrandsPublicStoreSearchSuccessSchema,
      (slug: string, params: operations.BrandsPublicStoreSearchQuery) => {
        const path = operations.BrandsPublicStoreSearchPathSchema.strict().parse({ slug })
        const query = storeSearchQuerySchema.parse(params)
        const hasBbox = query.bbox !== null && query.bbox !== undefined
        const hasPlaceType = query.place_type !== null && query.place_type !== undefined
        return {
          url: `${publicBaseURL}/${path.slug}/stores/search`,
          options: {
            method: 'GET',
            params: operations.BrandsPublicStoreSearchQuerySchema.strict().parse({
              q: query.q,
              ...(query.lat === null || query.lat === undefined ? {} : { lat: query.lat }),
              ...(query.lng === null || query.lng === undefined ? {} : { lng: query.lng }),
              ...(hasBbox && hasPlaceType ? { bbox: query.bbox, place_type: query.place_type } : {})
            })
          }
        }
      },
      brandStoreSearchContract.fromParsed
    )
  }
}
